/**
 * CreateAccountForm.jsx
 * Created by michaelbray on 2/13/18.
 * Updated by Destin Frasier on 1/11/19.
 */

import React from 'react';
import PropTypes from 'prop-types';
import validator from 'validator';
import * as Icons from '../../../sharedComponents/icons/Icons';
import { FlexCol, FlexRow } from '../../../sharedComponents/FlexGrid';
import Button from '../../../sharedComponents/Button';
import Label from '../../../sharedComponents/Label';
import Checkbox from '../../../sharedComponents/Checkbox';
import Heading from '../../../sharedComponents/Heading';
import NotificationsWrapper from '../../../notifications/NotificationsWrapper';
import Recaptcha from '../../../sharedComponents/recaptcha/Recaptcha';
import EmailField from '../../../sharedComponents/TextField/EmailField';


const propTypes = {
    disableAccountFeatures: PropTypes.bool,
    errorText: PropTypes.string,
    signIn: PropTypes.func,
    confirmAccount: PropTypes.func,
    additionalChallengeRequired: PropTypes.bool,
    disableSignUpButton: PropTypes.bool
};

export default class CreateAccountForm extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            firstName: '',
            lastName: '',
            email: '',
            cellPhone: '',
            optIn: true
        };

        this.firstNameTextChange = this.firstNameTextChange.bind(this);
        this.lastNameTextChange = this.lastNameTextChange.bind(this);
        this.emailTextChange = this.emailTextChange.bind(this);
        this.cellPhoneTextChange = this.cellPhoneTextChange.bind(this);
        this.optInChange = this.optInChange.bind(this);
        this.submitForm = this.submitForm.bind(this);
    }

    componentDidMount() {
        this.firstName.focus();
    }

    firstNameTextChange(e) {
        this.setState({
            firstName: e.target.value
        });
    }

    lastNameTextChange(e) {
        this.setState({
            lastName: e.target.value
        });
    }

    emailTextChange(e) {
        this.setState({
            email: e.target.value
        });
    }

    cellPhoneTextChange(e){
        this.setState({
            cellPhone: e.target.value
        })
    }

    optInChange(e) {
        this.setState({
            optIn: e.target.checked
        })
    }

    submitForm(e) {
        e.preventDefault();

        this.props.confirmAccount({
            email: this.state.email,
            firstName: this.state.firstName,
            lastName: this.state.lastName,
            cellPhone: this.state.cellPhone,
            optIn: this.state.optIn
        });
    }

    render() {
        let error = null;
        let firstNameErr = null;
        let lastNameErr = null;
        let cellPhoneErr = null;

        let firstNameClass = "rec-acct-first";
        let lastNameClass = "rec-acct-last";
        let cellPhoneClass = "rec-acct-cell-phone"

        if (this.props.errorText) {
            error = (
                <div className="rec-notification-error">
                    <span className="rec-notification-icon">
                        <Icons.IconWarning />
                    </span>
                    <div className="rec-notification-body">
                        <p>{this.props.errorText}</p>
                    </div>
                </div>
            );

            if (validator.isEmpty(this.state.firstName)) {
                firstNameErr = (
                    <div className="rec-required">
                        <p>
                            <mark>*</mark> First name is required.
                        </p>
                    </div>
                );
                firstNameClass = "rec-acct-email rec-error";
            }

            if (validator.isEmpty(this.state.lastName)) {
                lastNameErr = (
                    <div className="rec-required">
                        <p>
                            <mark>*</mark> Last name is required.
                        </p>
                    </div>
                );
                lastNameClass = "rec-acct-email rec-error";
            }

            if ((validator.isEmpty(this.state.cellPhone) || !validator.isMobilePhone(this.state.cellPhone, 'any'))) {
                cellPhoneErr = (
                    <div className="rec-required">
                        <p>
                            <mark>*</mark> Must be a valid mobile phone number.
                        </p>
                    </div>
                );
                cellPhoneClass = "rec-acct-cell-phone rec-error"
            }
        }

        let recaptcha = null;
        if (this.props.additionalChallengeRequired) {
            recaptcha = (
                <div className="form-item-wrap">
                    <Recaptcha />
                </div>
            )
        }

        let signUpDisabled = null;
        if (this.props.disableAccountFeatures) {
            signUpDisabled = (
                <NotificationsWrapper noticeType="error" closeButton={false}>
                    <div className="rec-notification-title">
                    Please be aware that you cannot make or change any reservations nor can you log in to
                    your account or create a new one. Account access and reservations are expected to be available
                    starting on October 5 at 10 a.m. (Eastern Time).
                    </div>
                </NotificationsWrapper>
            );
        }

        return (
            <FlexRow justifyContent="center">
                <FlexCol sm={6} md={5} xl={4} className="rec-acct-modal-sign-up-body">
                    <Heading
                        headingLevel={1}
                        appearance="display"
                        className="rec-acct-modal-title"
                        id="createAccountModalHeading">
                        Sign Up
                    </Heading>

                    <div className="rec-acct-modal-existing-account">
                        <p>Already have an account?</p>
                        <Button
                            appearance="link"
                            disabled={this.props.disableAccountFeatures}
                            onClick={this.props.signIn}
                            title="Log In"
                            aria-label="Log In">
                            Log In
                        </Button>
                    </div>

                    {signUpDisabled}
                    {error}

                    <form onSubmit={this.submitForm}>
                        <div className="form-item-wrap">
                            <Label
                                htmlFor="rec-acct-first">
                                First Name
                            </Label>
                            <input
                                disabled={this.props.disableAccountFeatures}
                                ref={(firstName) => this.firstName = firstName}
                                onChange={(event) => this.firstNameTextChange(event)}
                                id="rec-acct-first"
                                className={firstNameClass} />
                            {firstNameErr}
                        </div>
                        <div className="form-item-wrap">
                            <Label
                                htmlFor="rec-acct-last">
                                Last Name
                            </Label>
                            <input
                                disabled={this.props.disableAccountFeatures}
                                ref={(lastName) => this.lastName = lastName}
                                onChange={(event) => this.lastNameTextChange(event)}
                                id="rec-acct-last"
                                className={lastNameClass} />
                            {lastNameErr}
                        </div>
                        <div className="form-item-wrap">
                            <EmailField 
                                id="rec-acct-email"
                                label="Email"
                                disabled={this.props.disableAccountFeatures}
                                value={this.state.email}
                                onChange={this.emailTextChange} />
                        </div>
                        <div className="form-item-wrap">
                            <Label
                                htmlFor="rec-acct-cell-phone">
                                Mobile Phone
                            </Label>
                            <input
                                disabled={this.props.disableAccountFeatures}
                                ref={(cellPhone) => this.cellPhone = cellPhone}
                                onChange={(event) => this.cellPhoneTextChange(event)}
                                id="rec-acct-cell-phone"
                                className={cellPhoneClass} />
                            {cellPhoneErr}
                        </div>
                        <div className="form-item-wrap">
                            <Checkbox
                                label="Yes, please share stories that will inspire my next adventure."
                                id="rec-acct-newsletter-opt-in"
                                value="opt-in"
                                isChecked={this.state.optIn}
                                onChange={this.optInChange} />
                        </div>

                        {recaptcha}

                        <Button
                            size="lg"
                            shouldFitContainer
                            disabled={this.props.disableAccountFeatures || this.props.disableSignUpButton}
                            type="submit"
                            className="rec-acct-sign-up-btn"
                            title="Sign Up"
                            aria-label="Sign Up">
                            Sign Up
                        </Button>
                    </form>
                    <p className="text-small">By creating an account, I accept Recreation.gov&#39;s&nbsp;<a href='/disclaimers'>Terms of Use</a> and <a href='/privacy-policy'>Privacy Policy</a>.
                    </p>
                </FlexCol>
            </FlexRow>
        );
    }
}

CreateAccountForm.propTypes = propTypes;
